import { useEffect, useRef, useState, type FC, type SVGProps } from 'react';
import { FormInputProps } from '@/@types/form.types';
import EListPopupPickerWithSearchV2 from '@/components/everridge/EListPopupPickerWithSearchV2';
import { useOrderServicesLookupApi } from '@/features/orders/hooks/useOrderServicesLookupApi';
import { GetOrderServicesLookupListItem } from '@/features/orders/types/orders-dto';
import CurrencyValue from '@/features/finances/components/CurrencyValue';
import { FieldValues } from 'react-hook-form';

type Props<T extends FieldValues> = {
	selectVariant?: 'single' | 'multi';
	showSearch?: boolean;
	icon?: FC<SVGProps<SVGSVGElement>>;
	addResetButton?: boolean;
} & FormInputProps<T>;

const resolveServicePrice = (item: GetOrderServicesLookupListItem) =>
	item.price ?? 0;

const EServiceLookup = <T extends FieldValues>(props: Props<T>) => {
	const [searchValue, setSearchValue] = useState('');
	const {
		placeholder = 'Select service',
		label = 'Service',
		addResetButton = false,
	} = props;

	const loaderRef = useRef<HTMLDivElement>(null);

	const {
		orderServicesLookupList,
		fetchOrderServicesLookupListNextPage,
		hasOrderServicesLookupListNextPage,
		isFetchingOrderServicesLookupListNextPage,
	} = useOrderServicesLookupApi({ search: searchValue });

	useEffect(() => {
		if (
			!loaderRef.current ||
			!hasOrderServicesLookupListNextPage ||
			isFetchingOrderServicesLookupListNextPage
		)
			return;

		const scrollableContainer =
			loaderRef.current.closest('.overflow-y-scroll');

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					fetchOrderServicesLookupListNextPage();
				}
			},
			{
				root: scrollableContainer,
				rootMargin: '100px',
			},
		);

		const current = loaderRef.current;
		observer.observe(current);

		return () => observer.unobserve(current);
	}, [
		fetchOrderServicesLookupListNextPage,
		hasOrderServicesLookupListNextPage,
		isFetchingOrderServicesLookupListNextPage,
	]);

	return (
		<EListPopupPickerWithSearchV2
			{...props}
			label={label}
			placeholder={placeholder}
			list={orderServicesLookupList ?? []}
			searchValue={searchValue}
			setSearchValue={setSearchValue}
			ref={loaderRef}
			addResetButton={addResetButton}
			selectVariant='single'
			selectedAppearance='outline'
			renderSelected={(items: GetOrderServicesLookupListItem[]) => {
				const item = items[0];
				if (!item) return null;

				return (
					<div className='flex min-w-0 flex-1 items-center justify-between gap-3'>
						<span className='truncate text-sm font-medium text-popover-1000'>
							{item.name ?? ''}
						</span>
						<span className='shrink-0 text-sm text-popover-800'>
							<CurrencyValue
								value={resolveServicePrice(item)}
								asset='other'
							/>
						</span>
					</div>
				);
			}}
			renderItem={(item: GetOrderServicesLookupListItem) => (
				<div className='flex w-full items-start justify-between gap-3'>
					<div className='flex min-w-0 flex-col gap-0.5'>
						<span className='truncate text-sm font-medium'>
							{item.name ?? ''}
						</span>
						{!!item.description && (
							<span className='truncate text-xs text-popover-800'>
								{item.description}
							</span>
						)}
					</div>
					<span className='shrink-0 text-sm text-popover-800'>
						<CurrencyValue
							value={resolveServicePrice(item)}
							asset='other'
						/>
					</span>
				</div>
			)}
		/>
	);
};

export default EServiceLookup;
